/**
 * Zentrales UTM- und Conversion-Tracking für BEKAJ BAU.
 *
 * Beim ersten Besuch werden UTM-Parameter, referrer und landing page
 * dauerhaft im localStorage gespeichert und bei jedem späteren Tracking-Event
 * automatisch mitgesendet.
 */
import axios from 'axios';
import { logger } from '@/utils/logger';
import { fireGoogleAdsConversion } from '@/utils/tracking';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'bekaj_utm_session';
const VISITOR_KEY = 'bekaj_visitor';   // gemeinsam mit attribution.js
const SESSION_KEY = 'bekaj_session';   // gemeinsam mit attribution.js

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

const safeParseJSON = (raw) => {
  try { return JSON.parse(raw); } catch { return null; }
};

/** Liest die persistente visitor_id / session_id, die attribution.js verwaltet. */
const getVisitorSession = () => {
  let visitor = null;
  let session = null;
  try { visitor = safeParseJSON(localStorage.getItem(VISITOR_KEY)); } catch { /* noop */ }
  try { session = safeParseJSON(sessionStorage.getItem(SESSION_KEY)); } catch { /* noop */ }
  return {
    visitor_id: visitor?.visitor_id || null,
    session_id: session?.session_id || null,
  };
};

/**
 * Liest aktuelle UTM-Parameter aus der URL.
 */
export const readUtmFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const out = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) out[k] = v;
  }
  return out;
};

/**
 * Beim ersten Besuch UTM + Referrer + Landing-Page speichern.
 * Spätere Besuche aktualisieren UTMs nur, wenn neue gesetzt sind.
 */
export const initUtmSession = () => {
  const existing = safeParseJSON(localStorage.getItem(STORAGE_KEY)) || {};
  const fresh = readUtmFromUrl();

  const merged = {
    ...existing,
    initial_referrer: existing.initial_referrer || document.referrer || '',
    initial_landing_page: existing.initial_landing_page || window.location.pathname + window.location.search,
    first_visit_timestamp: existing.first_visit_timestamp || new Date().toISOString(),
    ...fresh,           // aktuelle UTMs überschreiben alte (Last-Touch)
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  return merged;
};

/**
 * Liest gespeicherte Tracking-Session (oder initialisiert sie).
 */
export const getUtmSession = () => {
  const stored = safeParseJSON(localStorage.getItem(STORAGE_KEY));
  if (stored) return stored;
  return initUtmSession();
};

/**
 * Feuert ein Tracking-Event:
 *   1) dataLayer.push für GTM/GA4
 *   2) fbq() für Meta Pixel (falls vorhanden)
 *   3) POST /api/tracking/event ans eigene Backend
 *
 * Gibt ein Promise zurück, das nach erfolgreichem Backend-Call resolved,
 * spätestens aber nach `timeoutMs` (Default 500ms) - so blockiert das
 * Tracking nie länger als nötig vor einem Redirect.
 */
export const trackEvent = (eventName, opts = {}) => {
  const session = getUtmSession();
  const now = new Date().toISOString();

  const dlPayload = {
    event: eventName,
    event_type: opts.event_type || null,
    destination_url: opts.destination_url || null,
    page_url: window.location.href,
    referrer: document.referrer || '',
    timestamp: now,
    utm_source: session.utm_source || '',
    utm_medium: session.utm_medium || '',
    utm_campaign: session.utm_campaign || '',
    utm_content: session.utm_content || '',
    utm_term: session.utm_term || '',
    initial_referrer: session.initial_referrer || '',
    initial_landing_page: session.initial_landing_page || '',
    ...(opts.extra || {}),
  };

  // 1) GTM / GA4
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(dlPayload);
  } catch (e) {
    logger.warn('[tracking] dataLayer push failed', e);
  }

  // 2) Meta Pixel
  try {
    if (window.fbq) {
      const metaMap = {
        whatsapp_click: 'Contact',
        call_click: 'Contact',
        form_open: 'InitiateCheckout',
        form_submit: 'Lead',
        lead_thank_you_view: 'Lead',
      };
      const evt = metaMap[eventName];
      if (evt) {
        window.fbq('track', evt, { source: 'internal_tracking', ...dlPayload });
      }
    }
  } catch (e) {
    logger.warn('[tracking] fbq track failed', e);
  }

  // 2b) Google Ads Conversion (AW-16933113405) — nur für die 3 Lead-Aktionen
  // Consent + Dedup-Logik liegt im Helper.
  // Google Ads Conversions laufen ausschließlich über lib/tracking.js trackLead()
  // an den Klickstellen bzw. auf /danke — hier KEINE Conversion-Delegation mehr.

  // 3) Backend (mit Timeout race)
  const ids = getVisitorSession();
  const backendCall = axios.post(`${BACKEND}/api/tracking/event`, {
    event_name: eventName,
    event_type: opts.event_type || null,
    destination_url: opts.destination_url || null,
    page_url: window.location.href,
    referrer: document.referrer || '',
    user_agent: navigator.userAgent,
    initial_referrer: session.initial_referrer || '',
    initial_landing_page: session.initial_landing_page || '',
    first_visit_timestamp: session.first_visit_timestamp || null,
    utm_source: session.utm_source || null,
    utm_medium: session.utm_medium || null,
    utm_campaign: session.utm_campaign || null,
    utm_content: session.utm_content || null,
    utm_term: session.utm_term || null,
    visitor_id: ids.visitor_id,
    session_id: opts.session_id || ids.session_id,
    extra: opts.extra || {},
  }, { timeout: 5000 }).catch((e) => {
    logger.warn('[tracking] backend log failed', e?.message);
  });

  const timeoutMs = opts.timeoutMs ?? 500;
  return Promise.race([
    backendCall,
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
};

/** Hilfsfunktion: leitet weiter, NACHDEM Tracking gefeuert wurde (max. 500ms Wartezeit). */
export const trackAndRedirect = async (eventName, destinationUrl, opts = {}) => {
  await trackEvent(eventName, { ...opts, destination_url: destinationUrl });
  window.location.href = destinationUrl;
};
